import classNames from 'classnames';
import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import { MenuItem, MenuSimpleItem } from '../components/Menu';
import SVGIcon from '../components/SVGIcon';
import Toast from '../components/Toast';
import Tooltip from '../components/Tooltip';
import { ExternalSearchMode, UserType } from '../graphql/generated/graphql';
import clearIcon from '../icons/clear.svg';
import addedIcon from '../icons/prefilled/added.svg';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import UIActions from '../ui/UIActions';
import styles from './SearchPlatformMenuItem.module.scss';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

export enum Section {
  Available = 'AVAILABLE',
  Individual = 'INDIVIDUAL',
  Team = 'TEAM',
}

enum Mode {
  Both,
  TeamOnly,
  PersonalOnly,
}

const PLATFORM_FRAGMENT = gql(`
  fragment SearchPlatformMenuItem_platform on ExternalSearchPlatform {
    installModes
    personalAuthorized
    personalAuthUrl
    platform
    teamAuthorized
    teamAuthUrl
  }
`);

interface SearchPlatformMenuItemProps {
  onRemove: () => Promise<unknown>;
  type: Section;
  platform: DocumentType<typeof PLATFORM_FRAGMENT>;
}

const SearchPlatformMenuItem = ({
  onRemove,
  platform: {
    platform,
    installModes,
    personalAuthorized,
    personalAuthUrl,
    teamAuthorized,
    teamAuthUrl,
  },
  type,
}: SearchPlatformMenuItemProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === UserType.Admin,
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const authorized =
    type !== Section.Available && (personalAuthorized || teamAuthorized);

  let mode: Mode;
  let modeLabel = null;

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platform];
  if (!platformInfo) return null;
  const { icon, name } = platformInfo;

  const isPersonalSupported = installModes?.includes(
    ExternalSearchMode.Personal,
  );
  const isTeamSupported = installModes?.includes(ExternalSearchMode.TeamWide);

  if (isPersonalSupported && isTeamSupported) {
    mode = Mode.Both;
    modeLabel = teamAuthorized ? 'Individual' : 'Team or Individual';
  } else if (isPersonalSupported) {
    mode = Mode.PersonalOnly;
    modeLabel = 'Individual';
  } else if (isTeamSupported) {
    mode = Mode.TeamOnly;
    modeLabel = 'Team';
  }

  const logoNode = icon && (
    <div className={styles.logo}>
      <ExternalBrandLogo height={20} {...icon} />
      {authorized && (
        <SVGIcon className={styles.addedIcon}>{addedIcon}</SVGIcon>
      )}
    </div>
  );

  const clearButton = type === Section.Individual && (
    <Button
      className={styles.clearIcon}
      onClick={(e) => {
        e.stopPropagation(); // so user can clear multiple services without popover closing
        onRemove().then(() => {
          dispatch(
            UIActions.pushToast(
              // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
              ({ onDismiss }: { onDismiss: () => unknown }) => (
                <Toast
                  onDismiss={onDismiss}
                  level="success"
                  message={`${name} has been removed from your search results.`}
                />
              ),
            ),
          );
        });
      }}
    >
      <SVGIcon size={10}>{clearIcon}</SVGIcon>
    </Button>
  );

  const addIntegration = () => {
    const open = (link?: string | null) => {
      if (link) {
        document.location.href = link;
      }
    };

    if (mode === Mode.Both && !personalAuthorized && teamAuthorized) {
      dispatch(
        UIActions.confirmModal({
          title: 'Add personal integration?',
          message: `${name} search is already enabled teamwide. This will be superseded by your own account, which will usually return more results when you search.`,
          confirmButtonText: 'Add personal',
          onConfirm: () => open(personalAuthUrl),
        }),
      );
    } else if (!isAdmin) {
      open(personalAuthUrl);
    } else if (mode === Mode.PersonalOnly) {
      open(personalAuthUrl);
    } else if (mode === Mode.TeamOnly) {
      open(teamAuthUrl);
    } else if (mode === Mode.Both && personalAuthorized && !teamAuthorized) {
      open(teamAuthUrl);
    } else if (
      mode === Mode.Both &&
      !personalAuthorized &&
      !teamAuthorized &&
      teamAuthUrl
    ) {
      dispatch(
        UIActions.confirmModal({
          title: 'Add personal integration?',
          message: (
            <>
              You can also enable {name} search for your entire team in your{' '}
              <a href={teamAuthUrl}>integration settings</a>.
            </>
          ),
          confirmButtonText: 'Add personal',
          onConfirm: () => open(personalAuthUrl),
        }),
      );
    }
  };

  const children = (
    <div
      className={classNames(styles.container, {
        [styles.authorized]: authorized,
      })}
    >
      <div className={styles.content}>
        {logoNode}
        <span className={styles.name}>{name}</span>
      </div>

      {clearButton}
    </div>
  );

  if (type === Section.Available)
    return (
      <MenuItem
        onClick={() => {
          addIntegration();
        }}
        wrap
        right={<span className={styles.platformModes}>{modeLabel}</span>}
      >
        {children}
      </MenuItem>
    );

  if (type === Section.Team && personalAuthorized)
    return (
      <MenuSimpleItem>
        <Tooltip
          tooltip={`Your team account is superseded by your personal ${name} account`}
          placement="bottom"
          trigger="hover"
        >
          {children}
        </Tooltip>
      </MenuSimpleItem>
    );

  return <MenuSimpleItem>{children}</MenuSimpleItem>;
};

export default SearchPlatformMenuItem;
